/**
 * Wire mappers for the Forms API (Pmp.Forms.Api). Unlike the Application
 * API, the Forms API serialises its enums as integers (matching
 * Pmp.Domain.Models.Enumerations ordinals) and models template questions as
 * `elements` without sections. These helpers translate between that contract
 * and the UI's application-template shapes so everything downstream of
 * FormApiService stays unchanged.
 */

import {
  ApplicationTemplateDto as FullTemplateDto,
  CreateApplicationTemplateDto,
  CreateFormElementDto,
  FormElementDto,
  FormElementType,
  QuestionResponseOptionDto,
  TemplateStatus,
  ValidatorMap
} from '@formrails/shared/data-access';
import { ApplicationTemplateDto as TemplateListItemDto, FormTemplateType } from '../models';

// --- Wire DTOs (Pmp.Forms.Api /FormTemplate) ---

export interface FormTemplateElementWireDto {
  /**
   * The question's own id, which a reportable question shares with its row in the element
   * library, and which every stored response is keyed by.
   */
  id: string;
  order: number;
  displayNumber: string | null;
  type: number;
  labelText: string;
  helperText: string | null;
  tooltip: string | null;
  placeholder: string | null;
  additionalInfo: string;
  semanticTags?: string[];
  validators: ValidatorMap | null;
  isSearchable: boolean;
  options: QuestionResponseOptionDto[] | null;
}

export interface UpsertFormTemplateWireDto {
  formTemplateType: number;
  name: string;
  description: string;
  version: number;
  status: number;
  elements: FormTemplateElementWireDto[];
}

export interface FormTemplateResponseWireDto {
  id: string;
  formTemplateType: number;
  name: string;
  description: string;
  version: number;
  status: number;
  elements: FormTemplateElementWireDto[] | null;
  totalQuestions: number;
  displayName: string;
  deleted: boolean;
  lastUpdatedBy: string | null;
  lastUpdatedOn: string;
}

// --- Enum ordinal maps (source of truth: Pmp.Domain.Models.Enumerations) ---

/** UI FormTemplateType string ↔ Pmp.Domain.FormTemplateType ordinal. */
const FORM_TYPE_TO_INT: Record<FormTemplateType, number> = {
  Application: 0,
  Assessment: 1,
  EligibilityQuestionnaire: 2, // domain name: Eligibility
  PreAssessment: 3, // domain name: Preassessment
  Survey: 4,
  UserProfile: 5
};

const INT_TO_FORM_TYPE: Record<number, FormTemplateType> = Object.fromEntries(
  Object.entries(FORM_TYPE_TO_INT).map(([k, v]) => [v, k as FormTemplateType])
);

/**
 * UI TemplateStatus string ↔ Pmp.Domain.TemplateStatus ordinal.
 *
 * 3 (Approved) and 4 (PendingApproval) were retired when sign-off moved onto
 * the template's promotion. `parseTemplateStatus` still reads them, since
 * older records may carry them, and treats both as Draft: neither meant live.
 */
const STATUS_TO_INT: Record<TemplateStatus, number> = {
  Draft: 0,
  Published: 1,
  Archived: 2
};

const INT_TO_STATUS: Record<number, TemplateStatus> = Object.fromEntries(
  Object.entries(STATUS_TO_INT).map(([k, v]) => [v, k as TemplateStatus])
);

/**
 * UI FormElementType string → Pmp.Domain.FormElementType ordinal.
 * UI aliases collapse onto their canonical domain value. Types with no
 * domain representation (Section, Toggle) are absent — sending them to the
 * Forms API is an error surfaced to the caller.
 */
const ELEMENT_TYPE_TO_INT: Partial<Record<FormElementType, number>> = {
  Title: 0,
  HelperText: 1,
  Dropdown: 2,
  DropdownList: 2,
  DynamicDropdown: 2,
  Radio: 3,
  Checkbox: 4,
  CheckboxList: 4,
  TextArea: 5,
  Textbox: 6,
  TextBox: 6,
  Date: 7,
  Email: 8,
  Password: 9,
  Tel: 10,
  // 11 is Criterion, which belongs to assessment matrices rather than forms.
  Confirmation: 12
};

const INT_TO_ELEMENT_TYPE: Record<number, FormElementType> = {
  0: 'Title',
  1: 'HelperText',
  2: 'Dropdown',
  3: 'Radio',
  4: 'Checkbox',
  5: 'TextArea',
  6: 'Textbox',
  7: 'Date',
  8: 'Email',
  9: 'Password',
  10: 'Tel',
  12: 'Confirmation'
};

export function formTypeToInt(type: FormTemplateType): number {
  return FORM_TYPE_TO_INT[type];
}

/**
 * Decode a wire form-type that may arrive as an ordinal (per the OpenAPI
 * schema) or an enum name (domain spelling included: "Eligibility",
 * "Preassessment").
 */
export function parseFormType(value: number | string | null | undefined): FormTemplateType {
  if (typeof value === 'number') return INT_TO_FORM_TYPE[value] ?? 'Application';
  if (!value) return 'Application';
  const name = value.toLowerCase();
  const aliases: Record<string, FormTemplateType> = {
    application: 'Application',
    assessment: 'Assessment',
    eligibility: 'EligibilityQuestionnaire',
    eligibilityquestionnaire: 'EligibilityQuestionnaire',
    preassessment: 'PreAssessment',
    survey: 'Survey',
    userprofile: 'UserProfile'
  };
  return aliases[name] ?? 'Application';
}

/** Decode a wire template status that may be an ordinal or an enum name. */
export function parseTemplateStatus(value: number | string | null | undefined): TemplateStatus {
  if (typeof value === 'number') return INT_TO_STATUS[value] ?? 'Draft';
  if (!value) return 'Draft';
  const name = value.toLowerCase();
  const byName = (Object.keys(STATUS_TO_INT) as TemplateStatus[]).find(
    s => s.toLowerCase() === name
  );
  return byName ?? 'Draft';
}

/** Decode a wire element type that may be an ordinal or an enum name. */
function parseElementType(value: number | string | null | undefined): FormElementType {
  if (typeof value === 'number') return INT_TO_ELEMENT_TYPE[value] ?? 'Textbox';
  if (!value) return 'Textbox';
  const name = value.toLowerCase();
  const byName = (Object.keys(ELEMENT_TYPE_TO_INT) as FormElementType[]).find(
    t => t.toLowerCase() === name
  );
  return byName ?? 'Textbox';
}

// --- Request mapping ---

/** Convert an application-template create/update payload to the Forms API shape. */
export function toUpsertFormTemplateDto(
  dto: CreateApplicationTemplateDto,
  formType: FormTemplateType
): UpsertFormTemplateWireDto {
  const unsupported = dto.applicationTemplateFormElements
    .map(e => e.type)
    .filter(t => ELEMENT_TYPE_TO_INT[t] === undefined);
  if (unsupported.length > 0) {
    throw new Error(
      `The following question types are not supported by ${formType} forms: ${[...new Set(unsupported)].join(', ')}.`
    );
  }

  return {
    formTemplateType: formTypeToInt(formType),
    name: dto.name,
    description: dto.description,
    version: dto.version,
    status: STATUS_TO_INT[dto.status],
    elements: dto.applicationTemplateFormElements.map(toWireElement)
  };
}

function toWireElement(el: CreateFormElementDto): FormTemplateElementWireDto {
  return {
    // The same id the element library was given for this question, so a response can be traced
    // back to the question that asked it and to its definition.
    id: el.formElementId,
    order: el.order,
    displayNumber: el.displayNumber,
    type: ELEMENT_TYPE_TO_INT[el.type] as number,
    labelText: el.labelText,
    helperText: el.helperText,
    tooltip: el.tooltip,
    placeholder: el.placeholder,
    additionalInfo: el.additionalInfo,
    validators: el.validators,
    isSearchable: el.isSearchable,
    options: el.options
  };
}

// --- Response mapping ---

/** Forms API template → the full template DTO the loader consumes. */
export function fromFormTemplateResponse(wire: FormTemplateResponseWireDto): FullTemplateDto {
  return {
    id: wire.id,
    name: wire.name,
    description: wire.description ?? '',
    status: parseTemplateStatus(wire.status),
    applicationTemplateFormElements: (wire.elements ?? []).map(fromWireElement),
    sections: [],
    // Assessment matrices are an application-template concept; the Forms API
    // has no equivalent.
    assessmentMatrixIds: [],
    lastUpdatedOn: wire.lastUpdatedOn,
    lastUpdatedBy: wire.lastUpdatedBy ?? ''
  };
}

function fromWireElement(el: FormTemplateElementWireDto, index: number): FormElementDto {
  return {
    // Kept as stored, so editing a template leaves its questions identifiable. A template saved
    // before the API carried ids has none; one is synthesised for the session and persisted by
    // the next save.
    formElementId: el.id ?? syntheticElementId(index),
    labelText: el.labelText,
    order: el.order ?? index,
    displayNumber: el.displayNumber,
    type: parseElementType(el.type),
    additionalInfo: el.additionalInfo ?? '',
    options: el.options ?? [],
    validators: el.validators ?? {},
    helperText: el.helperText,
    tooltip: el.tooltip,
    placeholder: el.placeholder,
    isSearchable: el.isSearchable ?? false
  };
}

/** Forms API template → the list-row DTO the selector modal consumes. */
export function fromFormTemplateListItem(wire: FormTemplateResponseWireDto): TemplateListItemDto {
  return {
    id: wire.id,
    name: wire.name,
    description: wire.description ?? undefined,
    status: parseTemplateStatus(wire.status) as TemplateListItemDto['status'],
    version: wire.version ?? 0,
    totalQuestions: wire.totalQuestions ?? wire.elements?.length ?? 0,
    displayName: wire.displayName || wire.name,
    lastUpdatedOn: wire.lastUpdatedOn,
    lastUpdatedBy: wire.lastUpdatedBy ?? '',
    formTemplateType: parseFormType(wire.formTemplateType)
  };
}

function syntheticElementId(index: number): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `form-element-${Date.now()}-${index}`;
}
